import React, { useCallback } from 'react';
import { EventCategory, trackEvent } from './analytics';

// Safe way to check environment for browser compatibility
const isDevelopment = import.meta.env.DEV;
const skipAnalyticsValidation = import.meta.env.VITE_SKIP_ANALYTICS_VALIDATION === 'true';
const debugAnalytics = import.meta.env.VITE_DEBUG_ANALYTICS === 'true';

// Define event types for better type safety
export interface AnalyticsEventData {
  category: string;
  action: string;
  label: string;
  value?: number;
  context?: Record<string, any>;
}

// Create a queue for failed analytics events
const analyticsQueue: AnalyticsEventData[] = [];
const MAX_QUEUE_SIZE = 100;
let isProcessingQueue = false;

/**
 * Custom hook for tracking analytics events
 */
export function useAnalyticsTracking(
  category: string,
  action: string,
  label: string,
  context?: Record<string, any>
) {
  return useCallback(() => {
    safeTrackEvent({
      category,
      action,
      label,
      context
    });
  }, [category, action, label, context]);
}

/**
 * Validates if an event label follows naming conventions
 * Returns true if valid, false if invalid
 */
export function validateEventLabel(label: string, category: string): boolean {
  // Skip validation in production or when explicitly disabled
  if (!isDevelopment || skipAnalyticsValidation) {
    return true;
  }

  const patterns = {
    [EventCategory.Navigation]: /^(nav|mobile)_[a-z0-9_]+$/,
    [EventCategory.Service]: /^(service|feature)_[a-z0-9_]+$/,
    [EventCategory.Contact]: /^(contact|form)_[a-z0-9_]+$/,
    [EventCategory.Engagement]: /^[a-z0-9]+_[a-z0-9_]+$/,
    // Add patterns for restored categories
    [EventCategory.Survey]: /^(survey|form)_[a-z0-9_]+$/,
    [EventCategory.Auth]: /^(auth|login|signup|password)_[a-z0-9_]+$/,
    [EventCategory.Admin]: /^admin_[a-z0-9_]+$/
  };

  const pattern = patterns[category as keyof typeof patterns];
  
  if (!pattern) {
    console.warn(`Unknown analytics category: ${category}`);
    return false;
  }
  
  const isValid = pattern.test(label);
  
  if (!isValid) {
    console.warn(
      `Invalid analytics label format: "${label}" for category "${category}". ` +
      `Expected format: ${patterns[category as keyof typeof patterns]}`
    );
  }
  
  return isValid;
}

/**
 * Safely track an event with error handling and offline support
 */
export function safeTrackEvent(eventData: Omit<AnalyticsEventData, 'action'> & { action?: string }) {
  // Skip tracking in server environment
  if (typeof window === 'undefined') {
    return;
  }

  // Check for analytics consent
  const consentKey = 'cookie-consent';
  const savedConsent = localStorage.getItem(consentKey);
  const hasAnalyticsConsent = savedConsent ? JSON.parse(savedConsent).analytics : false;

  // If no consent, don't track even in queue
  if (!hasAnalyticsConsent) {
    if (isDevelopment && debugAnalytics) {
      console.warn('Analytics event not processed: User has not provided consent');
    }
    return;
  }

  // Set default action if not provided
  const data: AnalyticsEventData = {
    ...eventData,
    action: eventData.action || 'click'
  };

  // Validate label in development
  if (isDevelopment) {
    validateEventLabel(data.label, data.category);
  }

  // Debug logging if enabled
  if (isDevelopment && debugAnalytics) {
    console.group('Analytics Event');
    console.log('Category:', data.category);
    console.log('Action:', data.action);
    console.log('Label:', data.label);
    if (data.value !== undefined) console.log('Value:', data.value);
    if (data.context) console.log('Context:', data.context);
    console.groupEnd();
  }

  try {
    // Check if online
    if (navigator.onLine) {
      trackEvent({
        category: data.category,
        action: data.action,
        label: data.label,
        value: data.value
      });
      
      // Process any queued events if we're online
      if (analyticsQueue.length > 0 && !isProcessingQueue) {
        processAnalyticsQueue();
      }
    } else {
      // Queue the event for later if offline
      queueAnalyticsEvent(data);
    }
  } catch (error) {
    console.error('Error tracking analytics event:', error);
    // Queue the event in case of error
    queueAnalyticsEvent(data);
  }
}

/**
 * Add an event to the analytics queue
 */
function queueAnalyticsEvent(eventData: AnalyticsEventData) {
  // Maintain max queue size by removing oldest events if needed
  if (analyticsQueue.length >= MAX_QUEUE_SIZE) {
    analyticsQueue.shift();
  }
  
  analyticsQueue.push(eventData);
  
  // Store queue in localStorage for persistence
  try {
    localStorage.setItem('analyticsQueue', JSON.stringify(analyticsQueue));
  } catch (e) {
    console.error('Failed to store analytics queue:', e);
  }
}

/**
 * Process queued analytics events
 */
function processAnalyticsQueue() {
  if (!navigator.onLine || analyticsQueue.length === 0 || isProcessingQueue) {
    return;
  }
  
  isProcessingQueue = true;
  
  const processNextEvent = () => {
    if (analyticsQueue.length === 0) {
      isProcessingQueue = false;
      localStorage.removeItem('analyticsQueue');
      return;
    }
    
    const eventData = analyticsQueue.shift()!;
    
    try {
      trackEvent({
        category: eventData.category,
        action: eventData.action,
        label: eventData.label,
        value: eventData.value
      });
      
      // Update localStorage
      localStorage.setItem('analyticsQueue', JSON.stringify(analyticsQueue));
      
      // Process next event with small delay to avoid overwhelming the analytics service
      setTimeout(processNextEvent, 100);
    } catch (error) {
      console.error('Error processing queued event:', error);
      // Re-queue the event if processing failed
      analyticsQueue.unshift(eventData);
      isProcessingQueue = false;
    }
  };
  
  processNextEvent();
}

/**
 * Initialize analytics queue from localStorage and 
 * set up online/offline event listeners
 */
export function initAnalytics() {
  // Skip in server environment
  if (typeof window === 'undefined') {
    return;
  }
  
  // Load queued events from localStorage
  try {
    const storedQueue = localStorage.getItem('analyticsQueue');
    if (storedQueue) {
      const parsedQueue = JSON.parse(storedQueue);
      if (Array.isArray(parsedQueue)) {
        analyticsQueue.push(...parsedQueue);
      }
    }
  } catch (e) {
    console.error('Failed to load analytics queue:', e);
  }
  
  // Setup online/offline event handlers
  window.addEventListener('online', () => {
    if (analyticsQueue.length > 0 && !isProcessingQueue) {
      processAnalyticsQueue();
    }
  });
  
  // Process queue on startup if online
  if (navigator.onLine && analyticsQueue.length > 0) {
    processAnalyticsQueue();
  }
} 